import {makeStyles} from "@material-ui/core"
import {ModernTheme} from "../../../../../theme/modern/tokens"
import {COARSE, NO_ZOOM_FONT, TOUCH_MIN, touchTarget} from "../../../../../theme/modern/touchTargets"

/**
 * The native select chevron, tinted to the current skin.
 *
 * Built here rather than pasted as a literal data URI because the prototype's
 * hard-coded `%238C99AC` is the light-mode ink, and it disappears against the
 * reading-room panel. encodeURIComponent is what keeps the `#` in the colour
 * from terminating the URL.
 *
 * @param color
 */
function chevron(color: string): string {
    const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="8" height="5" viewBox="0 0 8 5">` +
        `<path d="M1 1l3 3 3-3" stroke="${color}" stroke-width="1.4" fill="none" stroke-linecap="round"/>` +
        `</svg>`;

    return `url('data:image/svg+xml;utf8,${encodeURIComponent(svg)}')`
}

/** Ported from the prototype's `pages/Record/record.css` `.toolbar` block. */
export default makeStyles((theme: ModernTheme) => ({
    toolbar: {
        height: theme.modern.toolbarHeight,
        flex: "none",
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "0 14px",
        background: theme.modern.panel,
        borderBottom: `1px solid ${theme.modern.line}`,
        overflow: "hidden",

        "& > *": {flex: "0 0 auto"},

        // the bar is a fixed 42 px, which cannot contain a 44 px control; it
        // sizes to its contents on touch instead
        [COARSE]: {height: "auto", minHeight: theme.modern.toolbarHeight, padding: "6px 12px"},
    },
    scroller: {
        flex: "1 1 auto",
        minWidth: 0,
        display: "flex",
        alignItems: "center",
        gap: "8px",
        overflowX: "auto",
        overflowY: "hidden",

        "&  > *": {flex: "0 0 auto"},
        scrollbarWidth: "thin",
        scrollbarColor: `${theme.modern.line} transparent`,
        "&::-webkit-scrollbar": {height: 6},
        "&::-webkit-scrollbar-track": {background: "transparent"},
        "&::-webkit-scrollbar-thumb": {background: theme.modern.line, borderRadius: 3},

        [COARSE]: {
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {display: "none"},
        },
        "@media (max-width:1280px)": {
            "& kbd": {display: "none"},
            "& $toggle": {fontSize: 0, gap: 0, padding: "0 8px"},
        },
    },
    toolbar__compact: {
        height: "auto",
        minHeight: 0,
        padding: "6px 8px",
        background: "transparent",
        border: "none",
        borderBottom: "none",
        overflowX: "auto",
        scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        scrollBehavior: "smooth",
        "&::-webkit-scrollbar": {display: "none"},

        [COARSE]: {minHeight: 0, padding: "6px 8px"},
    },
    mobilebar: {
        flex: "none",
        position: "relative",
        padding: "8px 10px 5px",
        background: theme.modern.panel,
        borderBottom: `1px solid ${theme.modern.line}`,
    },
    frame: {
        position: "relative",
        borderRadius: theme.modern.radius,
        border: `1px solid ${theme.modern.line}`,
        background: theme.modern.panel2,
        overflow: "hidden",
    },
    fade: {
        position: "absolute",
        top: 0,
        bottom: 0,
        width: 40,
        display: "flex",
        alignItems: "center",
        pointerEvents: "none",
        opacity: 0,
        transition: "opacity .18s ease",
        border: "none",
        margin: 0,
        padding: 0,
        background: "none",
        font: "inherit",
        cursor: "default",

        "&:focus-visible": {
            outline: `2px solid ${theme.modern.accent}`,
            outlineOffset: -2,
        },

        "& svg": {
            width: 15,
            height: 15,
            flex: "none",
            color: theme.modern.ink3,
        },
    },
    fade__on: {opacity: 1},
    fade__clickable: {
        pointerEvents: "auto",
        cursor: "pointer",
    },
    fade__left: {
        left: 0,
        justifyContent: "flex-start",
        paddingLeft: 3,
        background: `linear-gradient(to right, ${theme.modern.panel2} 42%, ${theme.modern.panel2}00 100%)`,
    },
    fade__right: {
        right: 0,
        justifyContent: "flex-end",
        paddingRight: 3,
        background: `linear-gradient(to left, ${theme.modern.panel2} 42%, ${theme.modern.panel2}00 100%)`,
    },
    "@keyframes hint_left": {
        "0%,100%": {transform: "translateX(0)"},
        "50%": {transform: "translateX(-3px)"},
    },
    "@keyframes hint_right": {
        "0%,100%": {transform: "translateX(0)"},
        "50%": {transform: "translateX(3px)"},
    },
    hint__left: {
        "& svg": {animation: "$hint_left 1.5s ease-in-out infinite"},
        "@media (prefers-reduced-motion: reduce)": {"& svg": {animation: "none"}},
    },
    hint__right: {
        "& svg": {animation: "$hint_right 1.5s ease-in-out infinite"},
        "@media (prefers-reduced-motion: reduce)": {"& svg": {animation: "none"}},
    },

    dots: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "5px",
        height: 13,
    },
    dot: {
        flex: "none",
        width: 5,
        height: 5,
        borderRadius: "50%",
        background: theme.modern.ink3,
        opacity: .35,
        transition: "width .18s ease, opacity .18s ease, background-color .18s ease",
    },
    dot__active: {
        width: 13,
        borderRadius: 3,
        background: theme.modern.accent,
        opacity: 1,
    },

    spacer: {
        flex: "1 1 0",
        minWidth: 8,
    },
    vsep: {
        width: 1,
        height: 20,
        background: theme.modern.line,
        margin: "0 4px",
    },

    toggle: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        height: 30,
        padding: "0 10px",
        borderRadius: 7,
        border: `1px solid ${theme.modern.line}`,
        background: theme.modern.panel2,
        color: theme.modern.ink2,
        fontFamily: theme.modern.sans,
        fontWeight: 500,
        fontSize: 12,
        whiteSpace: "nowrap",
        cursor: "pointer",

        "& svg": {width: 13, height: 13, flex: "none"},
        "&:active": {borderColor: theme.modern.ink3, color: theme.modern.ink},

        [COARSE]: touchTarget({height: "auto", padding: "0 14px"}),
    },
    toggle__active: {
        background: theme.modern.accentSoft,
        borderColor: theme.modern.accentLine,
        color: theme.modern.accent,
        fontWeight: 500,
    },

    tgroup: {
        display: "inline-flex",
        alignItems: "center",
        gap: "6px",
        whiteSpace: "nowrap",
        // No `display:none` on narrow viewports: unlike the read-only readouts
        // these replaced, layout / speed / gain are the controls a reviewer
        // actually reaches for. The toolbar scrolls sideways instead.
    },
    tlabel: {
        font: `600 10px/1 ${theme.modern.sans}`,
        letterSpacing: ".1em",
        textTransform: "uppercase",
        color: theme.modern.ink3,
    },
    tselect: {
        appearance: "none",
        MozAppearance: "none",
        WebkitAppearance: "none",
        font: `500 12px ${theme.modern.mono}`,
        color: theme.modern.ink,
        background: theme.modern.panel2,
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 7,
        padding: "5px 24px 5px 9px",
        cursor: "pointer",
        backgroundImage: chevron(theme.modern.ink3),
        backgroundRepeat: "no-repeat",
        backgroundPosition: "right 8px center",

        "&:focus": {
            outline: `2px solid ${theme.modern.accent}`,
            outlineOffset: -1,
        },

        // a native <select> zooms iOS on focus below 16 px exactly as a text
        // input does. touchTarget() is not used here: it sets display:inline-flex,
        // which a select must not have.
        [COARSE]: {
            fontSize: NO_ZOOM_FONT,
            minHeight: TOUCH_MIN,
            padding: "0 30px 0 12px",
        },
    },

    /**
     * −  100%  + as one segmented control, so it reads as a single value the
     * way the LAYOUT / SPEED / GAIN selects beside it do rather than as three
     * loose buttons.
     */
    zoom: {
        display: "inline-flex",
        alignItems: "center",
        border: `1px solid ${theme.modern.line}`,
        borderRadius: 7,
        background: theme.modern.panel2,
        overflow: "hidden",
    },
    zoom_btn: {
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: 26,
        height: 26,
        padding: 0,
        border: "none",
        background: "transparent",
        color: theme.modern.ink2,
        cursor: "pointer",

        "& svg": {width: 12, height: 12},

        "&:hover:not(:disabled)": {color: theme.modern.ink},
        "&:disabled": {opacity: .4, cursor: "default"},

        [COARSE]: {width: TOUCH_MIN, height: TOUCH_MIN},
    },
    zoom_value: {
        minWidth: 44,
        height: 26,
        padding: "0 4px",
        border: "none",
        borderLeft: `1px solid ${theme.modern.line}`,
        borderRight: `1px solid ${theme.modern.line}`,
        background: "transparent",
        font: `500 12px ${theme.modern.mono}`,
        color: theme.modern.ink,
        cursor: "pointer",

        "&:hover": {color: theme.modern.accent},

        [COARSE]: {height: TOUCH_MIN, minWidth: 52},
    },

    btn: {
        height: 30,
        borderRadius: 8,
        font: `600 12px ${theme.modern.sans}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "6px",
        border: `1px solid ${theme.modern.line}`,
        background: theme.modern.panel2,
        color: theme.modern.ink2,
        whiteSpace: "nowrap",
        padding: "0 14px",
        cursor: "pointer",

        "& svg": {width: 13, height: 13, flex: "none"},

        "&:hover": {
            borderColor: theme.modern.ink3,
            color: theme.modern.ink,
        },
        "&:active": {filter: "brightness(.94)"},

        "@media (max-width:1400px)": {padding: "0 10px"},

        // Approve and Approve+Next are in this class. They are the two most
        // consequential taps in the app, so they get the full target and sit
        // after the 1400 px padding rule that would otherwise narrow them.
        [COARSE]: touchTarget({height: "auto", padding: "0 18px"}),
    },
    btn__approve: {
        background: theme.modern.ok,
        borderColor: theme.modern.ok,
        color: theme.modern.onAccent,

        "&:hover": {
            filter: "brightness(1.06)",
            borderColor: theme.modern.ok,
            color: theme.modern.onAccent,
        },
    },
    btn__approve_next: {
        background: theme.modern.accent,
        borderColor: theme.modern.accent,
        color: theme.modern.onAccent,

        "&:hover": {
            filter: "brightness(1.06)",
            borderColor: theme.modern.accent,
            color: theme.modern.onAccent,
        },
    },
    btn__edit: {
        background: theme.modern.accentSoft,
        borderColor: theme.modern.accentLine,
        color: theme.modern.accent,

        "&:hover": {
            borderColor: theme.modern.accent,
            color: theme.modern.accent,
        },
    },

    approve_group: {
        flex: "none",
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        marginLeft: 8,
    },
}))
